import { useEffect, useState } from 'react'
import { Moon, Sun, Droplet } from 'lucide-react'
import { clsx } from 'clsx'

type Theme = 'dark' | 'dark-blue' | 'light'

const THEMES: { id: Theme; label: string; icon: React.ElementType; hint: string }[] = [
  { id: 'dark',      label: 'Dark',      icon: Moon,    hint: 'Original slate dark' },
  { id: 'dark-blue', label: 'Dark Blue', icon: Droplet, hint: 'Black + mid-blue + white' },
  { id: 'light',     label: 'Light',     icon: Sun,     hint: 'White + black + blue' },
]

const STORAGE_KEY = 'ctod.theme'

function applyTheme(theme: Theme) {
  const html = document.documentElement
  html.classList.remove('dark', 'dark-blue', 'light')
  html.classList.add(theme)
  try { localStorage.setItem(STORAGE_KEY, theme) } catch {}
}

export function loadInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Theme | null
    if (saved && ['dark', 'dark-blue', 'light'].includes(saved)) {
      applyTheme(saved)
      return saved
    }
  } catch {}
  applyTheme('dark')
  return 'dark' as Theme
}

export default function ThemeSwitcher({ compact = false }: { compact?: boolean }) {
  const [theme, setTheme] = useState<Theme>('dark')

  useEffect(() => {
    const html = document.documentElement
    const current = (['dark', 'dark-blue', 'light'] as Theme[]).find(t => html.classList.contains(t)) || 'dark'
    setTheme(current)
  }, [])

  const set = (t: Theme) => { applyTheme(t); setTheme(t) }

  if (compact) {
    // Vertical stack so all 3 options fit inside a narrow collapsed side rail
    return (
      <div className="flex flex-col items-center gap-0.5 rounded-md border border-border p-0.5">
        {THEMES.map(t => {
          const Icon = t.icon
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => set(t.id)}
              title={`${t.label} — ${t.hint}`}
              aria-label={t.label}
              className={clsx(
                'p-1 rounded transition-colors',
                theme === t.id
                  ? 'bg-surface-hover text-text-primary'
                  : 'text-text-muted hover:text-text-primary hover:bg-surface-hover',
              )}
            >
              <Icon size={13} />
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] uppercase tracking-wider text-text-muted px-2">Theme</div>
      <div className="flex flex-col gap-0.5">
        {THEMES.map(t => {
          const Icon = t.icon
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => set(t.id)}
              className={clsx(
                'flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors text-left',
                theme === t.id
                  ? 'bg-surface-hover text-text-primary'
                  : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
              )}
            >
              <Icon size={13} />
              <span>{t.label}</span>
              <span className="ml-auto text-[9px] text-text-muted">{t.hint}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
